/**
 * Pure list filtering for the Super Admin console (no I/O, no React).
 * Kept free of `@/` imports so vitest can load it without the Vite aliases.
 */

const norm = (v) => String(v == null ? '' : v).toLowerCase().trim();

/** Fields a merchant search matches against. */
export const MERCHANT_SEARCH_FIELDS = ['display_name', 'slug', 'legal_name', 'primary_host', 'contact_email', 'city', 'database_name'];

/**
 * Filter the real merchant list.
 *   status: '' | 'all' | a company status (active, suspended, provisioning_failed, …)
 *   plan:   '' | 'all' | a plan key
 *   q:      free text, matched case-insensitively against MERCHANT_SEARCH_FIELDS
 */
export function filterMerchants(rows, { status = '', plan = '', q = '' } = {}) {
  const term = norm(q);
  return (rows || []).filter((r) => {
    if (status && status !== 'all' && r.status !== status) return false;
    if (plan && plan !== 'all' && r.plan_key !== plan) return false;
    if (!term) return true;
    return MERCHANT_SEARCH_FIELDS.some((f) => norm(r[f]).includes(term));
  });
}

/**
 * Generic queue filter: exact match on each key of `where` (skipping empty or
 * 'all' values), plus free-text search over `fields`.
 */
export function filterRows(rows, where = {}, q = '', fields = []) {
  const term = norm(q);
  return (rows || []).filter((r) => {
    for (const [k, v] of Object.entries(where)) {
      if (v === '' || v == null || v === 'all') continue;
      if (r[k] !== v) return false;
    }
    if (!term) return true;
    return fields.some((f) => norm(r[f]).includes(term));
  });
}

/** { value: count } for one field, e.g. counts per status for filter chips. */
export function countBy(rows, field) {
  const out = {};
  for (const r of rows || []) {
    const k = r[field] == null ? '' : r[field];
    out[k] = (out[k] || 0) + 1;
  }
  return out;
}

/** Select options with counts: [{ value: 'all', label: 'All (n)' }, { value, label: 'Label (n)' }…]. */
export function optionsWithCounts(rows, field, labelFor = (v) => String(v).replace(/_/g, ' ')) {
  const counts = countBy(rows, field);
  return [
    { value: 'all', label: `All (${(rows || []).length})` },
    ...Object.keys(counts)
      .filter((k) => k !== '')
      .sort()
      .map((k) => ({ value: k, label: `${labelFor(k)} (${counts[k]})` })),
  ];
}
